import { CommonModule, formatDate } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnChanges, OnDestroy, SimpleChanges, inject, input, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { catchError, finalize, interval, of, Subscription } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';

import { Shipment } from '../../models/shipment';
import { ShipmentDelivery } from '../../models/delivery/shipment-delivery.model';
import { ShipmentService } from '../../shipment.service';
import {
    GpsLocation,
    GpsLocationHistoryPoint,
    GpsSelectOption,
    GpsTrackingSource,
    GpsVehicleEvent,
    GpsVehicleLive,
    DeliveryGpsHistoryWindow,
    DeliveryGpsTrackingSnapshot
} from '../../../fleet/gps-tracking/gps-tracking.model';
import { GpsMarkerAnimator } from '../../../fleet/gps-tracking/gps-marker-animator';
import { gpsTruckMarkerIcon } from '../../../fleet/gps-tracking/gps-truck-marker';

export interface DeliveryTrackingSummary {
    distance: string | null;
    eta: string | null;
    delayed: boolean;
    status: string | null;
}

/**
 * Owns the live GPS subscription and replay timer for a single delivery.
 * It deliberately has no access to the page's workflow or mutation actions.
 */
@Component({
    selector: 'app-delivery-tracking-panel',
    standalone: true,
    templateUrl: './delivery-tracking-panel.component.html',
    styleUrls: ['./delivery-tracking-panel.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        FormsModule,
        GoogleMapsModule,
        ButtonModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        SelectModule,
        TableModule,
        TagModule
    ]
})
export class DeliveryTrackingPanelComponent implements OnChanges, OnDestroy {
    readonly shipment = input.required<Shipment>();
    readonly delivery = input.required<ShipmentDelivery>();
    readonly deliveryId = input.required<number>();
    readonly summaryChange = output<DeliveryTrackingSummary>();

    private readonly shipmentService = inject(ShipmentService);

    gpsSources: GpsTrackingSource[] = [];
    selectedGpsSourceKey = '';
    gpsLive?: GpsVehicleLive;
    gpsEvents: GpsVehicleEvent[] = [];
    gpsHistory: GpsLocationHistoryPoint[] = [];
    gpsHistoryWindow?: DeliveryGpsHistoryWindow;
    gpsLoading = false;
    gpsPollingEnabled = true;
    replayIndex = 0;
    replayPlaying = false;
    replaySpeed = 1;
    currentReplayPoint?: GpsLocationHistoryPoint;
    gpsMapCenter: google.maps.LatLngLiteral = { lat: 24.7136, lng: 46.6753 };
    gpsMapZoom = 12;

    readonly options: google.maps.MapOptions = {
        mapTypeId: 'roadmap',
        zoomControl: true,
        scrollwheel: true,
        disableDoubleClickZoom: true,
        maxZoom: 15,
        minZoom: 8
    };
    readonly replaySpeeds: GpsSelectOption<number>[] = [
        { label: '1x', value: 1 },
        { label: '2x', value: 2 },
        { label: '10x', value: 10 },
        { label: '100x', value: 100 }
    ];
    private readonly derivedAlertEventTypes = new Set([
        'GPS_SIGNAL_LOST', 'GPS_SIGNAL_RESTORED', 'GPS_SIGNAL_ACQUIRED',
        'IGNITION_ON', 'IGNITION_OFF', 'MOVING', 'STOPPED'
    ]);
    private gpsPollSub?: Subscription;
    private replayTimer?: ReturnType<typeof setTimeout>;
    private followGpsMarker = false;
    private readonly gpsMarkerAnimator = new GpsMarkerAnimator(position => {
        if (this.followGpsMarker) {
            this.gpsMapCenter = position;
        }
    });

    ngOnChanges(changes: SimpleChanges): void {
        if ((changes['deliveryId'] || changes['delivery']) && this.deliveryId()) {
            this.resetForDelivery();
            this.load();
        }
    }

    ngOnDestroy(): void {
        this.gpsPollSub?.unsubscribe();
        this.pauseReplay();
        this.gpsMarkerAnimator.destroy();
    }

    load(showSpinner = true): void {
        const shipmentId = Number(this.shipment().shipmentId);
        if (!Number.isFinite(shipmentId) || shipmentId <= 0 || !this.deliveryId()) {
            this.gpsLive = undefined;
            this.gpsEvents = [];
            this.gpsHistory = [];
            this.gpsHistoryWindow = undefined;
            this.pauseReplay();
            this.summaryChange.emit({ distance: null, eta: null, delayed: false, status: null });
            return;
        }
        if (showSpinner) {
            this.pauseReplay();
            this.gpsLoading = true;
        }
        this.shipmentService.getDeliveryGpsTracking(shipmentId, this.deliveryId(), this.selectedGpsSourceKey || undefined)
            .pipe(catchError(() => of(undefined)), finalize(() => this.gpsLoading = false)).subscribe(snapshot => {
            const data = snapshot as DeliveryGpsTrackingSnapshot | undefined;
            this.gpsSources = data?.sources ?? [];
            this.gpsLive = data?.live ?? undefined;
            this.gpsEvents = this.sortGpsEvents(data?.events ?? []);
            this.gpsHistory = this.sortGpsHistory(data?.history ?? []);
            this.gpsHistoryWindow = data?.historyWindow;
            if (showSpinner) this.resetReplay(false); else this.syncReplayPointAfterHistoryRefresh();
            if (!this.currentReplayPoint) this.moveGpsMarker(this.gpsLocationPosition(this.gpsLive?.location), false, 700, this.gpsLive?.location);
            if (showSpinner) this.centerGpsMap();
            this.loadEta();
            this.configurePolling();
        });
    }

    onGpsSourceChange(): void { this.load(); }
    toggleGpsPolling(): void { this.gpsPollingEnabled = !this.gpsPollingEnabled; this.configurePolling(); }
    gpsSourceOptions(): GpsSelectOption[] { return [{ label: 'Best available', value: '' }, ...this.gpsSources.map(source => ({ label: this.displayGpsName(source.displayName), value: source.sourceKey }))]; }
    gpsRoutePath(): google.maps.LatLngLiteral[] { return this.gpsHistory.map(point => this.gpsPointPosition(point)).filter((position): position is google.maps.LatLngLiteral => position !== null); }
    gpsLiveMarkerPosition(): google.maps.LatLngLiteral | null { return this.gpsMarkerAnimator.position() ?? this.gpsLocationPosition(this.gpsLive?.location); }
    replayMarkerPosition(): google.maps.LatLngLiteral | null { return this.currentReplayPoint ? this.gpsMarkerAnimator.position() : null; }
    gpsVehicleMarkerOptions(selected = true): google.maps.MarkerOptions {
        const replaying = !!this.currentReplayPoint;
        return {
            icon: gpsTruckMarkerIcon({
                headingDegrees: this.gpsMarkerAnimator.headingDegrees(),
                selected,
                stale: !replaying && this.gpsLive?.liveStatus === 'STALE_LOCATION'
            }),
            zIndex: selected ? 40 : 24,
            optimized: true
        };
    }
    pickupLocation(): google.maps.LatLngLiteral | null { return this.locationPosition(this.shipment().sourceLocation?.location); }
    dropoffLocation(): google.maps.LatLngLiteral | null { return this.locationPosition(this.shipment().destinationLocation?.location); }
    vehicleLabel(): string { return this.delivery().deliveryVehicle?.plateNumber || '-'; }

    startReplay(): void { if (this.gpsHistory.length < 2) return; if (this.replayIndex >= this.gpsHistory.length - 1) this.setReplayIndex(0, true); this.replayPlaying = true; this.scheduleReplayStep(); }
    pauseReplay(): void { this.replayPlaying = false; if (this.replayTimer) { clearTimeout(this.replayTimer); this.replayTimer = undefined; } }
    resetReplay(shouldCenter = true): void { this.pauseReplay(); this.setReplayIndex(0, shouldCenter); }
    onReplayIndexChange(value: string | number): void { this.pauseReplay(); this.setReplayIndex(Number(value), true); }
    onReplaySpeedChange(): void { if (this.replayPlaying) this.scheduleReplayStep(); }
    replayProgress(): number { return this.gpsHistory.length <= 1 ? (this.gpsHistory.length ? 100 : 0) : Math.round((this.replayIndex / (this.gpsHistory.length - 1)) * 100); }
    replayMaxIndex(): number { return Math.max(this.gpsHistory.length - 1, 0); }
    derivedGpsAlerts(): GpsVehicleEvent[] { return this.gpsEvents.filter(event => this.derivedAlertEventTypes.has(event.eventType)); }
    gpsStatusLabel(status?: string | null): string { return ({ ACTIVE_LOCATION: 'Active', STALE_LOCATION: 'Stale', NO_GPS_MAPPING: 'No mapping', NO_LOCATION: 'No location' } as Record<string, string>)[status || ''] || status || 'Unavailable'; }
    gpsStatusSeverity(status?: string | null): 'success' | 'info' | 'warn' | 'danger' | 'secondary' { return status === 'ACTIVE_LOCATION' ? 'success' : status === 'STALE_LOCATION' ? 'warn' : status === 'NO_GPS_MAPPING' || status === 'NO_LOCATION' ? 'danger' : 'secondary'; }
    gpsEventSeverity(eventType?: string | null): 'success' | 'info' | 'warn' | 'danger' | 'secondary' { return eventType === 'GPS_SIGNAL_LOST' ? 'danger' : eventType === 'GPS_SIGNAL_RESTORED' || eventType === 'GPS_SIGNAL_ACQUIRED' ? 'success' : eventType === 'MOVING' || eventType === 'IGNITION_ON' ? 'info' : eventType === 'STOPPED' || eventType === 'IGNITION_OFF' ? 'secondary' : 'warn'; }
    gpsSourceName(sourceKey?: string | null): string { return !sourceKey ? 'Best available' : this.displayGpsName(this.gpsSources.find(item => item.sourceKey === sourceKey)?.displayName ?? sourceKey); }
    gpsCoordinateLabel(location?: { latitude?: number | string | null; longitude?: number | string | null } | null): string { const lat = Number(location?.latitude); const lng = Number(location?.longitude); return Number.isFinite(lat) && Number.isFinite(lng) ? `${lat.toFixed(6)}, ${lng.toFixed(6)}` : '-'; }
    gpsSpeedLabel(location?: { speedKph?: number | string | null } | null): string { return `${Number(location?.speedKph ?? 0).toFixed(1)} kph`; }
    gpsDateLabel(value?: string | null): string { return value ? formatDate(value, 'dd MMM yyyy, HH:mm', 'en-US') : '-'; }
    gpsHistoryWindowLabel(): string {
        const window = this.gpsHistoryWindow;
        if (!window?.available || !window.from || !window.to) {
            return window?.status === 'DELIVERY_NOT_STARTED'
                ? 'Unavailable — delivery has not entered In Progress'
                : 'Unavailable';
        }
        return `${formatDate(window.from, 'dd MMM yyyy, HH:mm', 'en-US')} - ${formatDate(window.to, 'dd MMM yyyy, HH:mm', 'en-US')}`;
    }
    gpsDisplayLocation(): GpsLocation | GpsLocationHistoryPoint | undefined { return this.gpsLive?.location ?? this.gpsHistory[this.gpsHistory.length - 1]; }

    private resetForDelivery(): void { this.gpsPollSub?.unsubscribe(); this.pauseReplay(); this.gpsMarkerAnimator.snapTo(null); this.gpsLive = undefined; this.gpsEvents = []; this.gpsHistory = []; this.gpsHistoryWindow = undefined; this.replayIndex = 0; this.currentReplayPoint = undefined; }
    private loadEta(): void { this.shipmentService.getDeliveryTrackingEta(this.deliveryId()).pipe(catchError(() => of(undefined))).subscribe(data => this.summaryChange.emit({ distance: data?.distance ?? null, eta: data?.duration ?? null, delayed: Number(data?.durationValue ?? 0) > 3600, status: this.gpsLive?.liveStatus ?? null })); }
    private configurePolling(): void { this.gpsPollSub?.unsubscribe(); if (!this.gpsPollingEnabled || !this.gpsHistoryWindow?.active) return; this.gpsPollSub = interval(15000).subscribe(() => { if (!document.hidden) this.load(false); }); }
    private locationPosition(location?: { latitude?: number | string | null; longitude?: number | string | null } | null): google.maps.LatLngLiteral | null { const lat = Number(location?.latitude); const lng = Number(location?.longitude); return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null; }
    private gpsLocationPosition(location?: GpsLocation | null): google.maps.LatLngLiteral | null { return this.locationPosition(location); }
    gpsPointPosition(point?: GpsLocationHistoryPoint | null): google.maps.LatLngLiteral | null { return this.locationPosition(point); }
    private centerGpsMap(): void { const position = this.gpsPointPosition(this.currentReplayPoint) || this.gpsLocationPosition(this.gpsLive?.location) || this.pickupLocation() || this.dropoffLocation(); if (position) { this.gpsMapCenter = position; this.gpsMapZoom = this.currentReplayPoint || this.gpsLive?.location ? 15 : 11; } }
    private setReplayIndex(index: number, shouldCenter: boolean, transitionMs = 700): void { if (!this.gpsHistory.length) { this.replayIndex = 0; this.currentReplayPoint = undefined; this.gpsMarkerAnimator.snapTo(null); return; } this.replayIndex = Math.min(Math.max(Math.trunc(index) || 0, 0), this.gpsHistory.length - 1); this.currentReplayPoint = this.gpsHistory[this.replayIndex]; this.moveGpsMarker(this.gpsPointPosition(this.currentReplayPoint), shouldCenter, transitionMs, this.currentReplayPoint); if (shouldCenter) this.gpsMapZoom = 15; }
    private syncReplayPointAfterHistoryRefresh(): void { if (!this.gpsHistory.length) { this.pauseReplay(); this.setReplayIndex(0, false); return; } const at = this.currentReplayPoint?.recordedAt; const matching = at ? this.gpsHistory.findIndex(point => point.recordedAt === at) : -1; this.setReplayIndex(matching >= 0 ? matching : Math.min(this.replayIndex, this.gpsHistory.length - 1), false); }
    private scheduleReplayStep(): void { if (this.replayTimer) clearTimeout(this.replayTimer); if (!this.replayPlaying || this.replayIndex >= this.gpsHistory.length - 1) { this.pauseReplay(); return; } const delay = this.replayDelayMs(this.gpsHistory[this.replayIndex], this.gpsHistory[this.replayIndex + 1]); this.replayTimer = setTimeout(() => { this.setReplayIndex(this.replayIndex + 1, true, Math.min(Math.max(delay * 0.85, 80), 700)); this.scheduleReplayStep(); }, delay); }
    private moveGpsMarker(position: google.maps.LatLngLiteral | null, shouldCenter: boolean, transitionMs = 700, sample?: Pick<GpsLocation, 'headingDegrees' | 'speedKph'> | GpsLocationHistoryPoint | null): void { this.followGpsMarker = shouldCenter; this.gpsMarkerAnimator.moveTo(position, sample?.headingDegrees, sample?.speedKph, transitionMs); }
    private replayDelayMs(current?: GpsLocationHistoryPoint, next?: GpsLocationHistoryPoint): number { const fallback = 1000 / Math.max(this.replaySpeed, 1); const start = current?.recordedAt ? new Date(current.recordedAt).getTime() : NaN; const end = next?.recordedAt ? new Date(next.recordedAt).getTime() : NaN; return Math.min(Math.max((Number.isFinite(start) && Number.isFinite(end) && end > start ? end - start : fallback) / Math.max(this.replaySpeed, 1), 120), 2000); }
    private sortGpsHistory(points: GpsLocationHistoryPoint[]): GpsLocationHistoryPoint[] { return [...points].sort((left, right) => this.gpsTimeValue(left.recordedAt) - this.gpsTimeValue(right.recordedAt)); }
    private sortGpsEvents(events: GpsVehicleEvent[]): GpsVehicleEvent[] { return [...events].sort((left, right) => this.gpsTimeValue(right.eventTime) - this.gpsTimeValue(left.eventTime)); }
    private gpsTimeValue(value?: string | null): number { const time = value ? new Date(value).getTime() : NaN; return Number.isFinite(time) ? time : 0; }
    private displayGpsName(value?: string | null): string { return value ? value.replace(/PROLOGIX/g, 'CARGOMATE').replace(/ProLogix/g, 'CargoMate').replace(/Prologix/g, 'CargoMate').replace(/prologix/g, 'CargoMate') : '-'; }
}
